import { Component, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { plainToClass } from 'class-transformer';
import { Event } from '../../../model/event';
import { AdminService } from '../../../service/admin.service';
import { SidebarItem, AdminSidebarItem } from '../../../model/sidebar-item';
import {
  Dashboard, DashboardDataset, DashboardDatasetSize, DashboardGraph
} from 'src/app/model/dashboard';
import { EVENT_ADMIN_ICONS } from '../event-admin-icons';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';

class LinkListItem {
  link: string;
  label: string | { full: string, short: string };
  children?: Array<LinkListItem>;
}

@Component({
  selector: 'app-admin-event-view',
  templateUrl: './event-view.component.html',
  styleUrls: ['./event-view.component.scss'],
  // Painel de componentes desliza pela direita ao abrir/fechar (o botão de
  // toggle fica fixo na borda do painel e NÃO entra na animação).
  animations: [
    trigger('panelSlide', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('220ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('220ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
})
export class AdminEventViewComponent implements OnInit {
  event: Event;
  linkList: Array<LinkListItem>;

  dashboards: Array<Dashboard>;
  /** Índice da aba/dashboard atualmente ativo (two-way com o chair-tabs). */
  selectedDashboardIndex = 0;

  // ─── Modo de edição inline ─────────────────────────────────────────
  /** Página em modo de edição (toggle pelo 2º botão do header). */
  editMode = false;
  /** Componentes (datasets) disponíveis pra incluir, agrupados por categoria. */
  datasetMenu: { category: string, sets: DashboardDataset[] }[] = [];
  /** Snapshot dos `dashboards` no instante em que se entrou em edit mode.
   *  Mudanças durante a edição (add/remove/reorder graph, rename/create/
   *  delete dashboard, resize, etc.) ficam LOCAIS — nada é persistido até
   *  o usuário clicar "Salvar e sair". Snapshot serve pra: (1) detectar
   *  dirty na saída e (2) restaurar tudo se "Descartar e sair". */
  private dashboardsSnapshot: Dashboard[] | null = null;
  /** Lista de IDs de dashboards REMOVIDOS durante a edição (mas que
   *  existiam no backend). Usado no `saveChanges` pra emitir os
   *  DELETEs correspondentes. */
  private deletedDashboardIds: number[] = [];

  // Mapa central de ícones (template referencia `icons.dashboard` no header).
  readonly icons = EVENT_ADMIN_ICONS;

  /** Opções de tamanho do gráfico ({ id: 'SMALL'|'MEDIUM'|'BIG', value: classe
   *  Bulma 'is-4'|'is-6'|'is-12' }) — usadas no menu de cada chart-container. */
  readonly sizeOptions = DashboardDatasetSize.toSelectable();

  constructor(
    private adminService: AdminService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.initEventView();
        });
      } else {
        this.initEventView();
      }
    });
  }

  initEventView() {
    this.createLinkList();
    this.adminService.getEventDashboards().subscribe(dashboards => {
      this.dashboards = dashboards;
      // Fallback pra 0: sem dashboard `principal`, findIndex retorna -1 e o
      // chair-tabs não renderiza painel nenhum (mostra a 1ª aba em vez disso).
      this.selectedDashboardIndex = Math.max(0, this.dashboards.findIndex(d => d.principal));

      // Assembles a key for each graph title (for translating purposes)
      this.dashboards.map(dashboard => dashboard.graphPosition.map(graph => {
        graph.title = this.graphTitleKey(graph);
        return graph;
      }));
    });
  }

  /** Chave i18n do título de um gráfico — `dataset.<categoria>.<nome>`. */
  private graphTitleKey(graph: DashboardGraph): string {
    return 'dataset.' + graph.dataset['category'] + '.' + graph.dataset['datasetName'];
  }

  /** Troca de aba: atualiza o índice atual e (fora do modo edição) salva o
   *  dashboard favorito — preserva o comportamento anterior, mas sem mexer no
   *  `principal` enquanto se edita. */
  onTabChange(index: number) {
    this.selectedDashboardIndex = index;
    if (!this.editMode) {
      this.saveFavoriteDashboard(index);
    }
  }

  saveFavoriteDashboard(currentIndexTab: number) {
    this.dashboards.forEach((dashboard, i) => {
      if (dashboard.id) {
        const isPrincipal = i === currentIndexTab;
        this.adminService.updateDashboard(dashboard.id, {
          title: dashboard.title,
          order: dashboard.order,
          principal: isPrincipal
        }).subscribe();
      }
    });
  }

  // ─── Modo de edição ────────────────────────────────────────────────

  /** Liga/desliga o modo de edição. Ao LIGAR: snapshot dos dashboards
   *  (pra suportar discard) e load do menu de componentes (1x). Ao
   *  DESLIGAR: se houver mudanças, abre diálogo Save/Discard; sem
   *  mudanças, fecha direto. */
  toggleEditMode() {
    if (!this.editMode) {
      this.enterEditMode();
      return;
    }
    // Fechar o painel = salvar direto (sem diálogo). Se não houver mudanças,
    // apenas sai do modo. Diálogo de save/discard só aparece quando o usuário
    // TENTA NAVEGAR PRA OUTRA PÁGINA com mudanças pendentes — via `canDeactivate`.
    if (this.isDirty()) {
      this.saveChanges();
    } else {
      this.exitEditMode();
    }
  }

  private enterEditMode(): void {
    // Deep clone via JSON — perde métodos de classe mas só usamos os dados.
    // Suficiente pra detectar dirty (igualdade JSON) e pra restaurar valores.
    this.dashboardsSnapshot = JSON.parse(JSON.stringify(this.dashboards));
    this.deletedDashboardIds = [];
    this.editMode = true;
    if (!this.datasetMenu.length) {
      this.loadDatasets();
    }
  }

  private exitEditMode(): void {
    this.editMode = false;
    this.dashboardsSnapshot = null;
    this.deletedDashboardIds = [];
  }

  /** True se algum dashboard mudou (ordem/título/principal/graphs) ou
   *  se algum dashboard foi criado/removido durante a edição. */
  private isDirty(): boolean {
    if (!this.dashboardsSnapshot) { return false; }
    if (this.deletedDashboardIds.length > 0) { return true; }
    return JSON.stringify(this.dashboards) !== JSON.stringify(this.dashboardsSnapshot);
  }

  /** Diálogo 3-way — usado APENAS quando o usuário tenta sair da página
   *  com mudanças pendentes (via `canDeactivate`). Retorna Observable<boolean>
   *  = pode-navegar: true (salvou), true (descartou), false (cancelou).
   *  O saveChanges/discardChanges é executado ANTES de resolver o observable
   *  pra que a navegação só continue depois da persistência terminar. */
  private confirmExitWithChanges(): Observable<boolean> {
    const canNavigate$ = new Subject<boolean>();
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.dashboard.unsaved-title',
        content: 'admin.event.dashboard.unsaved-content',
        // 3-way: [Cancelar (neutral)] [Descartar (cancel/destructive)] [Salvar (confirm/primary)]
        neutral: 'forms.buttons.cancel',
        neutralValue: 'cancel',
        confirm: 'admin.event.dashboard.unsaved-save',
        cancel: 'admin.event.dashboard.unsaved-discard',
        cancelValue: 'discard',
        cancelStyle: 'destructive'
      }
    }).afterClosed().subscribe(result => {
      if (result === true)         { this.saveChanges();    canNavigate$.next(true);  }
      else if (result === 'discard') { this.discardChanges(); canNavigate$.next(true);  }
      // 'cancel' (novo botão) e undefined (X/ESC/backdrop) → não navega
      else                            {                       canNavigate$.next(false); }
      canNavigate$.complete();
    });
    return canNavigate$.asObservable();
  }

  /** Gancho consumido pelo `UnsavedChangesGuard` — quando o usuário tenta
   *  sair da página estando em modo de edição COM mudanças, abre o 3-way
   *  save/discard/cancel. Sem mudanças, permite navegar direto. */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.editMode || !this.isDirty()) { return true; }
    return this.confirmExitWithChanges();
  }

  /** Persiste todas as mudanças do snapshot atual → backend. Fluxo em
   *  2 fases pra capturar ids de dashboards recém-criados:
   *   1. Deletes (em paralelo) + Creates (em paralelo).
   *   2. Updates (metadata + graphs) — depende dos ids retornados pelos
   *      creates da fase 1 (pra os dashboards novos saberem o id).
   *  Sem isso, reentrar em edit mode duplicaria os dashboards novos. */
  private saveChanges(): void {
    const deleteOps = this.deletedDashboardIds.map(id =>
      this.adminService.deleteDashboard(id)
    );
    const createTargets: Array<{ board: Dashboard, order: number }> = [];
    this.dashboards.forEach((d, order) => {
      d.order = order;
      if (!d.id) { createTargets.push({ board: d, order }); }
    });
    const createOps = createTargets.map(t => this.adminService.createDashboard(t.board));

    const phase1 = [...deleteOps, ...createOps];
    const runUpdates = () => {
      const updateOps = [];
      this.dashboards.forEach((d, order) => {
        if (!d.id) { return; } // ainda sem id após create falhar — pula
        updateOps.push(this.adminService.updateDashboard(d.id, {
          title: d.title, order, principal: d.principal
        }));
        const graphs = (d.graphPosition || []).map((g, gOrder) => ({
          id: g.id,
          title: g.title,
          size: DashboardDatasetSize.valueToKey(g.size as string),
          dataset: typeof g.dataset === 'object' ? (g.dataset as DashboardDataset).id : g.dataset,
          order: gOrder
        }));
        updateOps.push(this.adminService.editDashboardGraphs(d.id, graphs as any));
      });
      if (!updateOps.length) { this.exitEditMode(); return; }
      forkJoin(updateOps).subscribe(() => this.exitEditMode());
    };

    if (!phase1.length) { runUpdates(); return; }
    forkJoin(phase1).subscribe((results: any[]) => {
      // Atribui ids aos dashboards recém-criados (preservando referência
      // no array `this.dashboards` — `createTargets[i].board` aponta pro
      // mesmo objeto). Os primeiros resultados são deletes; os de
      // create vêm depois, na ordem de `createTargets`.
      const createResults = results.slice(deleteOps.length);
      createTargets.forEach((t, i) => {
        const created = createResults[i] as Dashboard;
        if (created?.id) { t.board.id = created.id; }
      });
      runUpdates();
    });
  }

  /** Descarta mudanças locais — restaura dashboards do snapshot. Como
   *  nada foi persistido durante a edição, basta restaurar o estado
   *  in-memory. */
  private discardChanges(): void {
    if (this.dashboardsSnapshot) {
      this.dashboards = this.dashboardsSnapshot.map(d => plainToClass(Dashboard, d));
      // Reposiciona o índice se o ativo foi removido durante a edição.
      if (this.selectedDashboardIndex >= this.dashboards.length) {
        this.selectedDashboardIndex = Math.max(0, this.dashboards.length - 1);
      }
    }
    this.exitEditMode();
  }

  /** Lista de componentes (datasets) disponíveis, agrupada por categoria —
   *  mesma fonte da página de configuração de dashboards. */
  private loadDatasets() {
    this.adminService.getAllDatasets().subscribe(datasets => {
      const menu: { category: string, sets: DashboardDataset[] }[] = [];
      datasets.forEach(set => {
        const found = menu.find(m => m.category === set.category);
        if (found) {
          found.sets.push(set);
        } else {
          menu.push({ category: set.category, sets: [set] });
        }
      });
      this.datasetMenu = menu;
    });
  }

  /** Cria um novo dashboard local (sem id) e seleciona-o. A persistência
   *  acontece em `saveChanges` (Salvar e sair). */
  createDashboard() {
    const board = plainToClass(Dashboard, {
      title: `Dashboard ${this.dashboards.length + 1}`,
      principal: false,
      order: this.dashboards.length,
      event: this.event.id,
      graphPosition: []
    } as Dashboard);
    this.dashboards.push(board);
    this.selectedDashboardIndex = this.dashboards.length - 1;
  }

  /** Exclui o dashboard `index` (clique no "x"), com confirmação.
   *  Em edit mode, é uma remoção LOCAL — se o dashboard já existia no
   *  backend (tem `id`), guarda o id em `deletedDashboardIds` pra emitir
   *  o DELETE em `saveChanges`. Descartar reverte tudo via snapshot. */
  confirmDeleteDashboard(index: number) {
    const dashboard = this.dashboards[index];
    if (!dashboard) { return; }

    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.dashboard.messages.confirm-dashboard-deletion',
        content: '',
        confirm: 'admin.event.dashboard.settings.buttons.delete-dashboard',
        destructive: true
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      if (dashboard.id) { this.deletedDashboardIds.push(dashboard.id); }
      this.dashboards.splice(index, 1);
      if (this.selectedDashboardIndex >= this.dashboards.length) {
        this.selectedDashboardIndex = Math.max(0, this.dashboards.length - 1);
      }
    });
  }

  /** Renomeia um dashboard (duplo-clique no título da aba). Em edit mode,
   *  só muta local; persist acontece em `saveChanges`. */
  renameDashboard(e: { index: number, label: string }) {
    const dashboard = this.dashboards[e.index];
    if (!dashboard) { return; }
    dashboard.title = e.label;
  }

  /** Altera o tamanho de um gráfico (is-4/is-6/is-12). Local-only — persist
   *  em `saveChanges`. */
  setGraphSize(dashboard: Dashboard, graph: DashboardGraph, size: string) {
    if (graph.size === size) { return; }
    graph.size = size;                          // classe Bulma → [ngClass] no template
  }

  /** Remove um gráfico de um dashboard. Local-only — persist em
   *  `saveChanges`. */
  deleteGraph(dashboard: Dashboard, graph: DashboardGraph) {
    const i = dashboard.graphPosition.indexOf(graph);
    if (i < 0) { return; }
    dashboard.graphPosition.splice(i, 1);
  }

  /** Reordena os gráficos de um dashboard (drag-and-drop). Local-only —
   *  persist em `saveChanges`. */
  dropChart(event: CdkDragDrop<unknown>, dashboard: Dashboard) {
    if (event.previousIndex === event.currentIndex) { return; }
    moveItemInArray(dashboard.graphPosition, event.previousIndex, event.currentIndex);
  }

  /** Reordena as abas/dashboards (drag pelo título). Local-only —
   *  persist em `saveChanges`. */
  reorderDashboard(event: { previousIndex: number, currentIndex: number }) {
    if (event.previousIndex === event.currentIndex) { return; }
    const active = this.dashboards[this.selectedDashboardIndex];
    moveItemInArray(this.dashboards, event.previousIndex, event.currentIndex);
    // Mantém selecionado o MESMO dashboard (segue de posição).
    this.selectedDashboardIndex = Math.max(0, this.dashboards.indexOf(active));
  }

  /** Persiste a nova ordem das abas (campo `order` de cada dashboard).
   *  Em DUAS passadas pra não violar a constraint `unique(event, order)`: como
   *  a nova ordem é uma permutação da antiga, atualizar um a um cria colisões
   *  transitórias (dois com o mesmo `order`). Passada 1 manda todos pra ordens
   *  temporárias fora do range; passada 2 grava as ordens finais. */
  private persistDashboardOrder() {
    const list = this.dashboards.filter(d => d.id);
    if (!list.length) { return; }
    const TEMP_OFFSET = 1000;

    const pass1 = list.map((d, i) => this.adminService.updateDashboard(d.id, {
      title: d.title,
      order: TEMP_OFFSET + i,
      principal: d.principal
    }));

    forkJoin(pass1).subscribe(() => {
      const pass2 = list.map(d => {
        const order = this.dashboards.indexOf(d);
        d.order = order;
        return this.adminService.updateDashboard(d.id, {
          title: d.title,
          order,
          principal: d.principal
        });
      });
      forkJoin(pass2).subscribe();
    });
  }

  /** Adiciona um componente (dataset) ao dashboard atual. Local-only —
   *  persist em `saveChanges`. */
  addComponent(set: DashboardDataset) {
    const current = this.dashboards[this.selectedDashboardIndex];
    if (!current) { return; }

    const graph = plainToClass(DashboardGraph, {
      dataset: set,
      size: 'MEDIUM',                       // @Transform converte p/ Bulma `is-6`
      title: 'dataset.' + set.category + '.' + set.datasetName
    });

    current.graphPosition = current.graphPosition || [];
    current.graphPosition.push(graph);
  }

  /** Persiste a lista de gráficos de um dashboard. NÃO usa `plainToClass` de
   *  propósito: o `@Transform` do `DashboardGraph.size` reconverteria a chave
   *  (`MEDIUM`) de volta pra classe Bulma (`is-6`), que o backend rejeita.
   *  Monta objeto simples com: `size` como CHAVE, `dataset` como id, `order` e
   *  `title` (obrigatório no backend).
   *
   *  Reorder vs add:
   *   • REORDER (todos os gráficos têm id): a nova ordem é uma permutação e o
   *     backend atualiza in-place por id → colide na constraint `unique(dashboard,
   *     order)` (retorna 500). Faz DUAS passadas: ordens temporárias fora do range
   *     e depois as finais.
   *   • ADD (há gráfico sem id): ordens não mudam (o novo vai pro fim) → sem
   *     colisão; PUT único (duas passadas DUPLICARIA o gráfico sem id). */
  private persistGraphs(dashboard: Dashboard) {
    if (!dashboard.id) { return; }
    const base = dashboard.graphPosition.map(g => ({
      id: g.id,
      title: g.title,
      size: DashboardDatasetSize.valueToKey(g.size as string),
      dataset: typeof g.dataset === 'object' ? (g.dataset as DashboardDataset).id : g.dataset
    }));
    const final = base.map((g, order) => ({ ...g, order }));
    const allHaveIds = dashboard.graphPosition.every(g => g.id != null);

    if (!allHaveIds) {
      this.adminService.editDashboardGraphs(dashboard.id, final as any).subscribe();
      return;
    }

    // Reorder: passada 1 (ordens temporárias, na sequência final) → passada 2.
    const TEMP_OFFSET = 1000;
    const temp = base.map((g, i) => ({ ...g, order: TEMP_OFFSET + i }));
    this.adminService.editDashboardGraphs(dashboard.id, temp as any).subscribe(() => {
      this.adminService.editDashboardGraphs(dashboard.id, final as any).subscribe();
    });
  }

  createLinkList(): void {
    const items = [...this.adminService.sidebar.topItems, ...this.adminService.sidebar.bottomItems];
    this.linkList = items.map(item => (
      {
        link: item.link,
        label: item.label,
        children: (<AdminSidebarItem[]>item.children) ?.map(e => ({
          link: e.link,
          label: e.label
        }))
      }
    ));
  }
}
